import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, Loader2, CheckCircle2, Zap, Users, Star } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const LeadCapture = ({ 
  source = 'landing_page', 
  variant = 'default',  // 'default', 'compact', 'hero'
  title = "Join the Roster",
  subtitle = "Get priority access to the Spring '26 cohort"
}) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    
    try {
      const response = await api.post('/academy/leads/capture', {
        email,
        name: name || null,
        source
      });
      
      if (response.data.success) {
        setSubmitted(true);
        toast.success('You\'re on the roster! Check your email for next steps.');
      }
    } catch (error) {
      if (error.response?.data?.detail?.includes('already')) {
        toast.info('You\'re already on the roster!');
        setSubmitted(true);
      } else {
        toast.error('Failed to join. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (submitted) {
    return (
      <div className={`${variant === 'hero' ? 'bg-gradient-to-r from-green-500/10 to-green-600/10 border-2 border-green-500/50' : 'bg-green-500/10 border border-green-500/30'} rounded-xl p-6 text-center`}>
        <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-green-500" />
        </div>
        <h3 className="text-xl font-black text-white mb-2">You're On The Roster!</h3>
        <p className="text-gray-400">
          Check your inbox for your Priority Access confirmation. 
          We'll notify you when Spring '26 enrollment opens.
        </p>
      </div>
    );
  }

  // Hero variant (large, prominent)
  if (variant === 'hero') {
    return (
      <div className="bg-gradient-to-r from-orange-500/10 via-amber-500/5 to-orange-500/10 border-2 border-orange-500/50 rounded-2xl p-8 text-center">
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
            <Zap className="w-6 h-6 text-orange-500" />
          </div>
        </div>
        
        <h2 className="text-3xl font-black text-white mb-2">{title}</h2>
        <p className="text-gray-400 mb-6 max-w-md mx-auto">{subtitle}</p>
        
        <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-3">
          <Input
            type="text"
            placeholder="Your Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-gray-800 border-gray-700 text-white h-12"
            data-testid="lead-name-input"
          />
          <Input
            type="email"
            placeholder="Your Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="bg-gray-800 border-gray-700 text-white h-12"
            data-testid="lead-email-input"
          />
          <Button 
            type="submit"
            disabled={loading}
            className="w-full bg-orange-500 hover:bg-orange-600 text-black font-black h-12 text-lg"
            data-testid="lead-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Mail className="w-5 h-5 mr-2" />
                Get Priority Access
              </>
            )}
          </Button>
        </form>
        
        <div className="flex items-center justify-center gap-6 mt-6 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            Limited Spots
          </span>
          <span className="flex items-center gap-1">
            <Star className="w-4 h-4" />
            Early Bird Pricing
          </span>
        </div>
      </div>
    );
  }

  // Compact variant (inline)
  if (variant === 'compact') {
    return (
      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="bg-gray-800 border-gray-700 text-white flex-1"
          data-testid="lead-email-compact"
        />
        <Button 
          type="submit"
          disabled={loading}
          className="bg-orange-500 hover:bg-orange-600 text-black font-bold whitespace-nowrap"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Join Roster'}
        </Button>
      </form>
    );
  }

  // Default variant
  return (
    <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
          <Mail className="w-5 h-5 text-orange-500" />
        </div>
        <div>
          <h3 className="text-white font-bold">{title}</h3>
          <p className="text-gray-500 text-sm">{subtitle}</p>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="bg-gray-800 border-gray-700 text-white"
          data-testid="lead-email-default"
        />
        <Button 
          type="submit"
          disabled={loading}
          className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Zap className="w-4 h-4 mr-2" />
              Get Priority Access
            </>
          )}
        </Button>
      </form>
    </div>
  );
};

export default LeadCapture;
